import type {Context} from '../types';
import type {JSONNode} from '@packages/shared/types/JSONNode';

const WAITING_ID = 'COMMERCE_ADD_TO_CART_BUTTON_WAITING';
const DEFAULT_ID = 'COMMERCE_ADD_TO_CART_BUTTON_DEFAULT';
const TEXTS_CATEGORY = 'Button label';
const DEFAULT_BUTTON_COPY = 'Add to Cart';

export default function moveCommerceAddToCartLoadingText(context: Context) {
  const {nodeMap, parentMap} = context.tree;
  const {jsonNode} = context.node;

  const wrapperNode = isAddToCartButton(jsonNode)
    ? findAddToCartWrapperAncestor(nodeMap, parentMap, jsonNode._id)
    : null;

  const wrapperLoadingText = wrapperNode
    ? getWrapperLoadingText(wrapperNode)
    : null;

  const buttonAlreadyHasWaitingCopy = hasWaitingCopy(jsonNode);

  const needed = wrapperLoadingText !== null && !buttonAlreadyHasWaitingCopy;

  return {
    needed,
    logData: {
      msg: 'Copying loadingText from ancestor CommerceAddToCartWrapper onto CommerceAddToCartButton.texts[WAITING]',
      meta: {
        buttonNodeId: jsonNode._id,
        wrapperNodeId: wrapperNode?._id,
      },
    },
    apply: () => {
      if (wrapperLoadingText === null) {
        return;
      }

      if (!jsonNode.data) {
        jsonNode.data = {};
      }

      const existingTexts = Array.isArray(jsonNode.data.texts)
        ? jsonNode.data.texts
        : null;

      if (!existingTexts || existingTexts.length === 0) {
        jsonNode.data.texts = [
          {
            id: DEFAULT_ID,
            name: 'Default text',
            copy: DEFAULT_BUTTON_COPY,
            category: TEXTS_CATEGORY,
          },
          {
            id: WAITING_ID,
            name: 'Waiting text',
            copy: wrapperLoadingText,
            category: TEXTS_CATEGORY,
          },
        ];
        return;
      }

      const waitingEntry = existingTexts.find((entry) => {
        return entry?.id === WAITING_ID;
      });

      if (waitingEntry) {
        waitingEntry.copy = wrapperLoadingText;
      } else {
        existingTexts.push({
          id: WAITING_ID,
          name: 'Waiting text',
          copy: wrapperLoadingText,
          category: TEXTS_CATEGORY,
        });
      }
    },
  };
}

const isAddToCartButton = (node: JSONNode) => {
  return node.type === 'CommerceAddToCartButton';
};

const isAddToCartWrapper = (node: JSONNode) => {
  return node.type === 'CommerceAddToCartWrapper';
};

const findAddToCartWrapperAncestor = (
  nodeMap: Map<string, JSONNode>,
  parentMap: Map<string, string>,
  nodeId: string
): JSONNode | null => {
  const parentId = parentMap.get(nodeId);
  if (!parentId) {
    return null;
  }

  const parentNode = nodeMap.get(parentId);
  if (!parentNode) {
    return null;
  }

  if (isAddToCartWrapper(parentNode)) {
    return parentNode;
  }

  return findAddToCartWrapperAncestor(nodeMap, parentMap, parentId);
};

// Returns the wrapper's `commerce.loadingText` value, or null when missing
// or empty. We don't special-case the default string: the button's per-node
// copy is the new source of truth, so mirroring whatever the wrapper holds
// (custom or not) gives a uniform post-migration state. The only guard is
// the `hasWaitingCopy` check on the button itself, which prevents clobbering
// new-UI edits.
const getWrapperLoadingText = (wrapperNode: JSONNode): string | null => {
  const value = wrapperNode.data?.commerce?.loadingText;

  if (typeof value !== 'string' || value.length === 0) {
    return null;
  }

  return value;
};

// Returns true when the button already has any non-empty WAITING copy.
// The button's per-node value always wins — whether it was set by a prior
// run of this patch or by edits made via the new MultiTextEdit UI — so we
// only pull from the legacy wrapper field when the button has no WAITING
// copy of its own.
const hasWaitingCopy = (buttonNode: JSONNode): boolean => {
  const texts = buttonNode.data?.texts;

  if (!Array.isArray(texts)) {
    return false;
  }

  const waiting = texts.find((entry) => {
    return entry?.id === WAITING_ID;
  });

  if (!waiting) {
    return false;
  }

  if (typeof waiting.copy !== 'string') {
    return false;
  }

  return waiting.copy.length > 0;
};
